import { type FC, memo, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  ImageNotSupported,
  RollerShades,
} from '@mui/icons-material';

import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';

import { type AuditWindowsFormEntry } from '#packages/data-collection/models/audit-windows-form';

const THUMB_SIZE = 72;

export type WindowsDirectionGroup = {
  id: string;
  label: string;
  entries: Array<{
    link: string;
    entry: AuditWindowsFormEntry;
    thumbnail: FileContent | null;
  }>;
};

export type AuditFormWindowsPageRendererProps = {
  groups: WindowsDirectionGroup[];
};

const EntryThumbnail: FC<{ content: FileContent | null }> = ({ content }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!content) {
      setSrc(null);
      return;
    }
    const blob = content instanceof Blob ? content : new Blob([content]);
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [content]);

  return (
    <Box
      sx={(theme) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2,
      })}>
      {src ? (
        <Box
          component="img"
          src={src}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageNotSupported
          sx={(theme) => ({ color: theme.palette.action.disabled })}
        />
      )}
    </Box>
  );
};

const formatDimensions = (entry: AuditWindowsFormEntry): string => {
  if (entry.isUsingMetrics) {
    const w = entry.widthInMeters;
    const h = entry.heightInMeters;
    if (w != null && h != null) return `${w}m × ${h}m`;
    if (w != null) return `${w}m wide`;
    if (h != null) return `${h}m tall`;
  } else {
    const wFt = entry.widthInFeet;
    const wIn = entry.widthInInches;
    const hFt = entry.heightInFeet;
    const hIn = entry.heightInInches;
    const wStr =
      wFt != null || wIn != null
        ? `${wFt ?? 0}'${wIn != null ? `${wIn}"` : ''}`
        : null;
    const hStr =
      hFt != null || hIn != null
        ? `${hFt ?? 0}'${hIn != null ? `${hIn}"` : ''}`
        : null;
    if (wStr && hStr) return `${wStr} × ${hStr}`;
    if (wStr) return `${wStr} wide`;
    if (hStr) return `${hStr} tall`;
  }
  return '—';
};

export const AuditFormWindowsPageRenderer: FC<AuditFormWindowsPageRendererProps> =
  memo(({ groups }) => (
    <Box flexGrow={1}>
      {groups.map((group) => (
        <Accordion key={group.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <RollerShades fontSize="small" />
              <Typography variant="body1">{group.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {group.entries.map(({ link, entry, thumbnail }, index) => (
                <ButtonBase
                  key={link}
                  component={Link}
                  to={link}
                  sx={{ display: 'block', textAlign: 'start' }}>
                  <Grid container alignItems="center" sx={{ p: 2 }}>
                    <Grid>
                      <EntryThumbnail content={thumbnail} />
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight="bold">
                        {entry.section || '—'}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="textSecondary">
                          {formatDimensions(entry)}
                        </Typography>
                        {entry.quantity != null && (
                          <Typography variant="caption" color="textSecondary">
                            × {entry.quantity}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                    <Grid>
                      <ChevronRight
                        sx={(theme) => ({ color: theme.palette.grey[400] })}
                      />
                    </Grid>
                  </Grid>
                  {index < group.entries.length - 1 && <Divider />}
                </ButtonBase>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
